import React, { FC, useState } from "react";
import { Tabs, TabsProps, Card, Button, Space, Input, Select } from "antd";
import { UserOutlined, ClockCircleOutlined, CloseCircleOutlined, HomeOutlined, PlusOutlined, SearchOutlined } from '@ant-design/icons';
import { useGetUsers } from "../utils/hooks";
import { UserProps } from "../utils/types";
import { getUsers } from "../utils/functions";
import AdminApprovalPanel from "../components/AdminApprovalPanel";
import UserManagement from "../components/UserManagement";
import DeniedUsersPanel from "../components/DeniedUsersPanel";
import { useNavigate } from 'react-router-dom';

const { Option } = Select;

interface UserPageProps {
  initialTab?: 'approved' | 'pending' | 'denied';
}

const User: FC<UserPageProps> = ({ initialTab }) => {
  const navigate = useNavigate();
  const [fetching, setFetching] = useState(true);
  const [users, setUsers] = useState<UserProps[] | undefined>();
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedRole, setSelectedRole] = useState<string>('all');
  
  // Get initial tab from localStorage if available, otherwise use prop or default
  const getInitialTab = () => {
    const storedTab = localStorage.getItem('userManagementTab') as 'approved' | 'pending' | null;
    if (storedTab) {
      localStorage.removeItem('userManagementTab'); // Clear after reading
      return storedTab;
    }
    return initialTab || "approved";
  };
  
  const [activeTab, setActiveTab] = useState(getInitialTab());

  useGetUsers(setUsers, setFetching);

  const refreshUsers = () => {
    getUsers(setUsers, setFetching);
  };

  // Separate users by approval status
  const pendingUsers = users?.filter(user => !user.approved && !user.denial_reason) || [];
  const approvedUsers = users?.filter(user => user.approved) || [];
  const deniedUsers = users?.filter(user => !user.approved && user.denial_reason) || [];

  const tabItems: TabsProps['items'] = [
    {
      key: 'approved',
      label: (
        <span>
          <UserOutlined />
          User Management ({approvedUsers.length})
        </span>
      ),
      children: (
        <UserManagement
          approvedUsers={approvedUsers}
          onRefreshUsers={refreshUsers}
          onEditUser={() => {}} // No-op since editing is handled within UserManagement
          loading={fetching}
          searchTerm={searchTerm}
          selectedRole={selectedRole}
        />
      )
    },
    {
      key: 'pending',
      label: (
        <span>
          <ClockCircleOutlined />
          Pending Approvals ({pendingUsers.length})
        </span>
      ),
      children: (
        <AdminApprovalPanel
          pendingUsers={pendingUsers}
          onRefreshUsers={refreshUsers}
          loading={fetching}
          searchTerm={searchTerm}
          selectedRole={selectedRole}
        />
      )
    },
    {
      key: 'denied',
      label: (
        <span>
          <CloseCircleOutlined />
          Denied Requests ({deniedUsers.length})
        </span>
      ),
      children: (
        <DeniedUsersPanel
          deniedUsers={deniedUsers}
          onRefreshUsers={refreshUsers}
          loading={fetching}
          searchTerm={searchTerm}
          selectedRole={selectedRole}
        />
      )
    }
  ];

  return (
    <div>
      {/* Header Card */}
      <Card style={{ marginBottom: 16 }}>
        <div style={{ padding: '16px 0' }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '8px' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <UserOutlined style={{ fontSize: '24px', color: '#1890ff' }} />
              <span style={{ fontSize: '24px', fontWeight: 600, color: '#000' }}>User Management</span>
            </div>
            <Space>
              <Button 
                type="primary" 
                icon={<PlusOutlined />}
                style={{ backgroundColor: '#1890ff' }}
              >
                Add New User
              </Button>
              <Button 
                icon={<HomeOutlined />} 
                onClick={() => navigate('/')}
                type="default"
              >
                Home
              </Button>
            </Space>
          </div>
          <span style={{ fontSize: '14px', color: '#666' }}>
            Manage user accounts, roles, and permissions for the employee management system
          </span>
        </div>
      </Card>

      {/* Summary Stats */}
      <div style={{ 
        display: 'grid', 
        gridTemplateColumns: 'repeat(3, 1fr)', 
        gap: '16px', 
        marginBottom: '24px' 
      }}>
        <Card style={{ 
          border: '2px solid #1890ff',
          borderRadius: '8px'
        }}>
          <div style={{ textAlign: 'center' }}>
            <div style={{ fontSize: '12px', fontWeight: 600, color: '#1890ff', marginBottom: '4px' }}>
              TOTAL USERS
            </div>
            <div style={{ fontSize: '32px', fontWeight: 700, color: '#1890ff' }}>
              {(users?.length || 0)}
            </div>
          </div>
        </Card>
        <Card style={{ 
          border: '2px solid #52c41a',
          borderRadius: '8px'
        }}>
          <div style={{ textAlign: 'center' }}>
            <div style={{ fontSize: '12px', fontWeight: 600, color: '#52c41a', marginBottom: '4px' }}>
              APPROVED USERS
            </div>
            <div style={{ fontSize: '32px', fontWeight: 700, color: '#52c41a' }}>
              {approvedUsers.length}
            </div>
          </div>
        </Card>
        <Card style={{ 
          border: '2px solid #faad14',
          borderRadius: '8px'
        }}>
          <div style={{ textAlign: 'center' }}>
            <div style={{ fontSize: '12px', fontWeight: 600, color: '#faad14', marginBottom: '4px' }}>
              PENDING APPROVAL
            </div>
            <div style={{ fontSize: '32px', fontWeight: 700, color: '#faad14' }}>
              {pendingUsers.length}
            </div>
          </div>
        </Card>
      </div>

      {/* Search & Filter */}
      <Card style={{ 
        marginBottom: '16px',
        background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
        border: 'none'
      }}>
        <div style={{ color: 'white', fontWeight: 600, fontSize: '16px', marginBottom: '16px' }}>
          Search & Filter
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 200px auto auto', gap: '16px', alignItems: 'end' }}>
          <div>
            <div style={{ color: 'white', marginBottom: '8px', fontSize: '14px' }}>Search</div>
            <Input
              placeholder="Name, email..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              style={{ borderRadius: '6px' }}
            />
          </div>
          <div>
            <div style={{ color: 'white', marginBottom: '8px', fontSize: '14px' }}>Role</div>
            <Select
              value={selectedRole}
              onChange={setSelectedRole}
              style={{ width: '100%' }}
              placeholder="All Roles"
            >
              <Option value="all">All Roles</Option>
              <Option value="Admin">Admin</Option>
              <Option value="User">User</Option>
            </Select>
          </div>
          <Button 
            type="primary" 
            icon={<SearchOutlined />}
            style={{ backgroundColor: 'rgba(255,255,255,0.2)', borderColor: 'rgba(255,255,255,0.3)' }}
          >
            Search
          </Button>
          <Button 
            type="default" 
            style={{ backgroundColor: 'rgba(255,255,255,0.1)', borderColor: 'rgba(255,255,255,0.3)', color: 'white' }}
            onClick={() => {
              setSearchTerm('');
              setSelectedRole('all');
            }}
          >
            Clear
          </Button>
        </div>
      </Card>

      {/* Main Content */}
      <Card>
        <Tabs
          activeKey={activeTab}
          onChange={(key) => setActiveTab(key as 'approved' | 'pending' | 'denied')}
          items={tabItems}
          size="large"
          tabBarStyle={{ marginBottom: 16 }}
          tabBarExtraContent={
            <div style={{ 
              backgroundColor: '#1890ff', 
              color: 'white', 
              padding: '4px 12px', 
              borderRadius: '16px',
              fontSize: '12px',
              fontWeight: 600
            }}>
              {users?.length || 0} Total
            </div>
          }
        />
      </Card>
    </div>
  );
};

export default User;